import {
  FC,
  ReactNode,
  ComponentType,
  cloneElement,
  ReactElement,
  useEffect,
  useState,
  PropsWithoutRef,
  forwardRef,
  Ref
} from 'react';
import styled, { css } from 'styled-components';

import { defaultThemeProp } from '../../theme';
import { Action, ForwardProps, OmitStrict } from '../../types';
import Flex, { FlexProps } from '../Flex';
import Actions from '../Actions';
import { FormControlProps } from '../FormControl';
import { useUID } from '../../hooks';
import Label, { LabelProps, StyledLabel } from '../Label';
import { calculateFontSize } from '../../styles';
import Icon from '../Icon';

export interface FormFieldProps
  extends OmitStrict<FormControlProps, 'placeholder' | 'value' | 'defaultValue'> {
  /** Pass a single form control component i.e. Input | TextArea | Select | etc.. */
  children: ReactElement | ReactElement[];
  /**
   * An id is required to connect a FormField's wrapping element, control element and info(aria-describedby) live region.
   * A random id will be generated if none is provided.
   * Since MOST FormControls should possess an id(excl. i.e RadioCheckGroup and others) this can be used.
   * The id prop will be used to generate the following:
   * - FormControl <-> Label association via a Label's htmlFor prop.
   * - FormField's wrapping element id {id}-field.
   * - FormField info element id ${id}-info.
   */
  id?: string;
  /**
   * Determines how the wrapping label should be rendered.
   * @default "label"
   */
  labelAs?: LabelProps['as'];
  /**
   * Overrides the for attribute on the label. If this is not set it will default to the forms id.
   * @default undefined
   */
  labelFor?: LabelProps['htmlFor'];
  /**
   * Sets the id of the label.
   * @default undefined
   */
  labelId?: LabelProps['id'];
  /**
   * Visually hides the label region.
   * @default false
   */
  labelHidden?: boolean;
  /**
   * Visually places the label after the input.
   * @default false
   */
  labelAfter?: boolean;
  /**
   * Wrapping HTML element tag. Renders as a fieldset for grouped elements i.e. RadioButtons/CheckboxGroup
   * @default "div"
   */
  as?: 'div' | 'fieldset' | ComponentType<any>;
  /**
   * Layout field elements inline in a row.
   * @default false
   */
  inline?: boolean;
  /** Optionally utilized by Input or Select. */
  actions?: Action[];
  /** Enables Flex container prop pass through. */
  container?: Exclude<FlexProps['container'], boolean>;
  /** Character remaining count. Typically used on Textareas only */
  charLimitDisplay?: ReactNode;
}

const StyledStatusIcon = styled(Icon)<{ status: NonNullable<FormFieldProps['status']> }>(
  ({ theme, status }) => {
    return css`
      height: 1em;
      width: 1em;
      color: ${theme.components['form-field'][status]['status-color']};
      vertical-align: baseline;
      margin-inline-end: 0.5ch;
    `;
  }
);

StyledStatusIcon.defaultProps = defaultThemeProp;

export const StyledFormFieldInfo = styled.div<Pick<FormControlProps, 'status'>>(
  ({
    status,
    theme: {
      base: { 'font-size': fontSize, 'font-scale': fontScale, spacing },
      components: { 'form-field': formField }
    }
  }) => {
    const { xxs: infoFontSize } = calculateFontSize(fontSize, fontScale);

    return css`
      max-width: max-content;
      margin-top: calc(0.25 * ${spacing});
      font-size: ${infoFontSize};
      word-break: break-word;
      ${status &&
      formField[status] &&
      css`
        color: ${formField[status]['status-color']};
      `}
    `;
  }
);

StyledFormFieldInfo.defaultProps = defaultThemeProp;

export const StyledFormField = styled.div<FormFieldProps>(props => {
  const {
    disabled,
    required,
    theme: {
      base: {
        palette: { urgent },
        'disabled-opacity': disabledOpacity,
        spacing
      }
    }
  } = props;

  return css`
    ${disabled &&
    css`
      opacity: ${disabledOpacity};
      -webkit-user-select: none;
      user-select: none;
    `}
    position: relative;
    border: 0;

    > ${StyledLabel} {
      margin-bottom: calc(0.25 * ${spacing});
      ${disabled &&
      css`
        cursor: not-allowed;
      `}

      &::after {
        display: ${required ? 'inline' : 'none'};
        content: '\\00a0*';
        vertical-align: top;
        color: ${urgent};
      }
    }
  `;
});

StyledFormField.defaultProps = defaultThemeProp;

const statusIconMap = { error: 'warn-solid', warning: 'warn', success: 'check' };

const FormField: FC<FormFieldProps & ForwardProps> = forwardRef(
  (props: PropsWithoutRef<FormFieldProps>, ref: Ref<HTMLElement>) => {
    const uid = useUID();
    const {
      children: controlElement,
      id = uid,
      as = 'div',
      label,
      labelAs = 'label',
      labelFor = id,
      labelId,
      labelHidden = false,
      labelAfter = false,
      info,
      status,
      charLimitDisplay,
      required = false,
      disabled = false,
      readOnly = false,
      inline = false,
      actions,
      container,
      ...restProps
    } = props;

    const labelAsLegend: boolean = labelAs === 'legend';

    const styledLabel = (
      <Label
        id={labelId}
        as={labelAs}
        htmlFor={labelAs === 'label' ? labelFor : undefined}
        labelHidden={labelHidden}
        onClick={(e: MouseEvent) => {
          if (readOnly) e.preventDefault();
        }}
        inline={inline}
      >
        {status && !labelHidden && (
          <StyledStatusIcon status={status} name={statusIconMap[status]} />
        )}
        {label}
      </Label>
    );

    /*
      We have to use an internal state and an effect to set the text value of info after the DOM element is rendered.
      This is to ensure screen readers will announce info on errors when role is set to alert.
      Needs testing to confirm. Currently the expected sequence would be:
      - FormField renders with no error and no info
      - user input triggers an error status
      - props passed set error status and provide info text
      - effect runs and sets live region state which renders and is announced
    */
    const [liveRegionInfo, setLiveRegionInfo] = useState<ReactNode>(null);

    useEffect(() => {
      setLiveRegionInfo(info);
    }, [info]);

    // fieldset or single form control element
    let content: FormFieldProps['children'] = labelAsLegend
      ? controlElement
      : cloneElement(controlElement as ReactElement, {
          'aria-describedby': info && `${id}-info`
        });

    if (actions) {
      content = (
        <Flex container={{ alignItems: 'center', gap: 0.5 }}>
          {content}
          <Actions items={actions} menuAt={3} />
        </Flex>
      );
    }

    let infoContent = info ? (
      /*
        Region for additional info, help or error message.
        Withholding aria-live="assertive" to avoid iOS issue. See below.
        https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/ARIA_Live_Regions#Preferring_specialized_live_region_roles
      */
      <StyledFormFieldInfo
        status={status}
        role={status === 'error' || status === 'warning' ? 'alert' : undefined}
        id={`${id}-info`}
      >
        {liveRegionInfo}
      </StyledFormFieldInfo>
    ) : undefined;

    if (charLimitDisplay) {
      infoContent = (
        <Flex container={{ justify: infoContent ? 'between' : 'end', gap: 1 }}>
          {infoContent}
          <Flex item={{ shrink: 0 }}>{charLimitDisplay}</Flex>
        </Flex>
      );
    }

    return (
      <Flex
        {...restProps}
        container={{
          direction: inline ? 'row' : 'column',
          alignItems: inline ? 'center' : undefined,
          ...container
        }}
        as={StyledFormField}
        id={`${id}-field`}
        forwardedAs={as}
        required={required}
        disabled={disabled}
        readOnly={readOnly}
        aria-describedby={labelAsLegend && info ? `${id}-info` : undefined}
        ref={ref}
      >
        {(labelAsLegend || !labelAfter) && styledLabel}
        {content}
        {!labelAsLegend && labelAfter && styledLabel}
        {infoContent}
      </Flex>
    );
  }
);

export default FormField;
